// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MITs
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  SecurityContext,
} from '@angular/core';
import { Column, DataRow, GroupedTableData } from '../../interfaces';
import { CommonModule } from '@angular/common';
import { UrlFormatPipe } from '@lfx/shared/pipes/url-format.pipe';
import { EllipsisPipe } from '@lfx/shared/pipes/ellipsis.pipe';
import { HoverTriggerComponent } from '@lfx/shared/components/hover-trigger/hover-trigger.component';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'lfx-new-table',
  templateUrl: './new-table.component.html',
  styleUrls: ['./new-table.component.scss'],
  imports: [CommonModule, UrlFormatPipe, EllipsisPipe, HoverTriggerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewTableComponent implements OnInit {
  @Input() columns: Column[] = [];
  @Input() data: DataRow[] = [];
  @Input() emptyDataMessage = 'No Data';
  @Input() freezeHeader = false;
  @Input() rowsMaxHeight = '300px';
  @Input() striped = true;
  @Input() version = 1; // legacy versioning, keeping input for now as most components are using this
  @Input() selectable = false;
  @Input() showGroupedRows = false;
  @Input() accordionBehavior = false;
  @Input() groupedData: GroupedTableData[] = [];
  @Input() activeSortColumn: string;
  @Input() groupEndContentTemplate: TemplateRef<any>; // optional: content to display at the end of each group
  @Input() pageSize = 10; // default page size
  @Input() useTablePagination = false; // flag to enable/disable table pagination

  @Output() columnTextClickAction = new EventEmitter<{
    row: DataRow;
    column: Column;
  }>();

  @Output() columnSortEmmitter = new EventEmitter<Column>();
  @Output() selectedRowEmitter = new EventEmitter<DataRow>();
  @Output() infoIconClickEmitter = new EventEmitter<Column>();

  selectedIndex = -1;
  currentPage = 1;

  constructor(private sanitizer: DomSanitizer) {}

  ngOnInit(): void {}

  /**
   * Get the currently displayed data based on pagination
   */
  get displayedData(): DataRow[] {
    if (!this.data || this.data.length === 0) {
      return [];
    }
    // Only apply pagination if useTablePagination is true
    if (!this.useTablePagination) {
      return this.data;
    }
    return this.data.slice(0, this.currentPage * this.pageSize);
  }

  /**
   * Get the total number of rows
   */
  get totalRows(): number {
    return this.data ? this.data.length : 0;
  }

  /**
   * Check if there are more rows to load
   */
  get hasMoreRows(): boolean {
    return this.totalRows > this.currentPage * this.pageSize;
  }

  /**
   * Show all rows by setting current page to display all data
   */
  showAll(): void {
    if (this.hasMoreRows) {
      this.currentPage = Math.ceil(this.totalRows / this.pageSize);
    }
  }

  handleColumnTextClick(item) {
    this.columnTextClickAction.emit(item);
  }

  toggleSortOrder(column: Column) {
    this.activeSortColumn = column.prop;

    if (column.sortDir === 'asc') {
      this.columnSortEmmitter.emit({ ...column, sortDir: 'desc' });

      return 'desc';
    } else {
      this.columnSortEmmitter.emit({ ...column, sortDir: 'asc' });

      return 'asc';
    }
  }

  handleRowClick(rowData, index) {
    if (this.selectable) {
      this.selectedIndex = index;
      this.selectedRowEmitter.emit(rowData);
    }
  }

  toggleAccordion(group) {
    if (this.accordionBehavior) {
      this.groupedData.forEach(item => {
        if (item.label !== group.label) {
          item.expanded = false;
        }
      });

      group.expanded = !group.expanded;
    } else {
      group.expanded = !group.expanded;
    }
  }

  /**
   * TrackBy function for columns - prevents unnecessary DOM recreation
   * Uses column.prop as unique identifier
   */
  trackByColumnProp(index: number, column: Column): string {
    return column.prop || `col-${index}`;
  }

  /**
   * TrackBy function for data rows - prevents unnecessary DOM recreation
   * Tries to use row.id if available, falls back to index
   */
  trackByRowId(index: number, row: DataRow): string | number {
    // Check for common id properties
    const id = row['id'];
    const _id = row['_id'];

    if (typeof id === 'string' || typeof id === 'number') return id;
    if (typeof _id === 'string' || typeof _id === 'number') return _id;

    // Fallback to index for rows without unique identifiers
    return index;
  }

  /**
   * TrackBy function for grouped data
   */
  trackByGroupLabel(index: number, group: GroupedTableData): string {
    return group.label || `group-${index}`;
  }

  /**
   * Sanitize header icon hover content to prevent XSS vulnerabilities
   * @param column - The column containing the header icon
   * @returns Sanitized HTML content or empty string
   */
  getSanitizedHeaderFaIconHoverContent(column: Column): string {
    const content = column.headerFaIcon?.hoverContent || column.headerFaIcon?.hoverText || '';
    return this.sanitizer.sanitize(SecurityContext.HTML, content) || '';
  }
}
